/**
 * Job creation orchestration. Takes a `IJobCreation` port and pure-domain
 * helpers and coordinates the multi-step creation pipeline:
 *
 * 1. Associate external growers (find existing or create new)
 * 2. Create new salesperson contacts
 * 3. Link salespersons ↔ branches
 * 4. Prepare jobs (flags, GeoJSON, zones, cores)
 *
 * `submitJobsUseCase` (in `@app/job/submitJobUseCases`) drives this pipeline.
 * Each helper is exported individually so they can be unit-tested in isolation
 * with mock `IJobCreation` implementations.
 */

import type { Branch } from "@domain/branch";

import type { Contact } from "@domain/contact";

import type { Deal } from "@domain/deal";

import type { Grower } from "@domain/grower";
import type { Job } from "@domain/job";
import type { Filter } from "@infra/interfaces/api/common";
import type { JobFlags } from "@domain/job";
import type { LogData } from "@rogoag/shared-ts";
import type { IJobCreation } from "@infra/interfaces/jobs/IJobCreation";
import type { ILogger } from "@infra/interfaces/logger/ILogger";
import { CREATE_NEW_SALESPERSON } from "@domain/constants";

// DEBUG-gated diagnostic logs in this file used to read from `@infra/implementations/config/env-flags`,
// but 3-Application is forbidden from importing implementations. Hardcoded to `false` here keeps the
// orchestration layer-clean; the same logs remain available via the always-emitted `logger.log` calls.
const DEBUG = false;
import { normalizePointsFC } from "@domain/sample";
import { applyCollectBoundary, cleanGeoJson, renumberZoneCores, handleZonesForCompositeJobs } from "@domain/job";
import {
	deduplicateNewSalespersons,
	splitFullName,
	buildJobFlags,
	mapExternalGrowersToJobs,
	filterNewExternalGrowers,
} from "@domain/job";
import { generateUUID } from "@domain/uuid";

/** Snapshot of caller state passed through the pipeline. */
export interface JobCreationContext {
	userEmail: string;
	userCompanyIds: string[];
	isAdmin: boolean;
	source?: JobFlags;
	deals: Deal[];
	logger: ILogger;
}

/**
 * Associates external growers with jobs by finding existing growers or creating new ones.
 * Mutates `job.growerIds` in place.
 */
export async function associateExternalGrowers(
	jobs: Partial<Job>[],
	ctx: JobCreationContext,
	api: IJobCreation,
	logData: LogData,
): Promise<void> {
	logData.function = "jobCreationOrchestration.associateExternalGrowers";

	const externalGrowers = jobs.map((job) => job.externalGrower).filter((g) => !!g);
	if (externalGrowers.length === 0) return;

	if (DEBUG) ctx.logger.log("[DEBUG] External growers to associate", { ...logData, externalGrowers });

	const existingGrowers = await api.getGrowersByFilter({
		type: "OR",
		fields: externalGrowers.map((g) => ({ externalId: g.id })),
	} as Filter<Grower>);

	if (DEBUG && existingGrowers.length > 0)
		ctx.logger.log("[DEBUG] Existing growers matching external IDs", { ...logData, existingGrowers });

	const newExternalGrowers = filterNewExternalGrowers(externalGrowers, existingGrowers);
	const companyId = ctx.userCompanyIds.at(0);
	const companyIds = companyId ? [companyId] : [];
	const newGrowers: Partial<Grower>[] = newExternalGrowers.map((g) => ({
		name: g.name,
		companyIds,
		externalId: g.id,
	}));

	if (DEBUG && newGrowers.length > 0)
		ctx.logger.log("[DEBUG] New external growers to create", { ...logData, newGrowers });

	ctx.logger.log("Associating external growers", logData);
	const createdGrowers = await api.createGrowers(newGrowers);

	if (DEBUG && createdGrowers.length > 0)
		ctx.logger.log("[DEBUG] Created external growers", { ...logData, createdGrowers });

	mapExternalGrowersToJobs(jobs, [...existingGrowers, ...createdGrowers]);
}

/**
 * Creates new salesperson contacts from jobs and links them back.
 * Mutates `job.salespersonIds` in place.
 */
export async function addNewSalespersons(
	jobs: Partial<Job>[],
	ctx: JobCreationContext,
	api: IJobCreation,
	logData: LogData,
): Promise<void> {
	const uniqueSalespersons = deduplicateNewSalespersons(jobs);
	if (uniqueSalespersons.length === 0) return;

	const newContacts: Partial<Contact>[] = uniqueSalespersons.map((sp) => {
		const { firstName, lastName } = splitFullName(sp.name);
		return {
			firstName,
			lastName,
			mobilePhone: sp.phone,
			companyIds: ctx.userCompanyIds,
			isSalesperson: true,
		};
	});

	logData.function = "jobCreationOrchestration.addNewSalespersons";
	ctx.logger.log("Adding new salespersons", logData);
	const contacts = await api.createContacts(newContacts);

	for (const job of jobs) {
		job.salespersonIds = job.salespersonIds?.filter((id) => id !== CREATE_NEW_SALESPERSON && !id.startsWith("temp-"));

		const newSalesperson = job.newSalesperson;
		if (!newSalesperson) continue;

		const salesperson = contacts.find(
			(c) => c.name.trim() === newSalesperson.name.trim() && c.mobilePhone.trim() === newSalesperson.phone.trim(),
		);
		if (!job.salespersonIds) job.salespersonIds = [];
		if (salesperson) job.salespersonIds.push(salesperson.id);
	}
}

/**
 * Links salespersons to branches (and branches to salespersons) for all jobs in the batch.
 */
export async function associateSalespersonsAndBranches(
	jobs: Partial<Job>[],
	api: IJobCreation,
	logData: LogData,
	logger: ILogger,
): Promise<void> {
	let contactsToUpdate: Contact[] = [];
	let branchesToUpdate: Branch[] = [];

	for (const job of jobs) {
		const validSalespersonIds = job.salespersonIds?.filter(
			(id) => id && !id.startsWith("temp-") && id !== CREATE_NEW_SALESPERSON,
		);
		if (!validSalespersonIds?.length || !job.branchIds?.length) continue;

		const [contacts, branches] = await Promise.all([
			api.getContactsByIds(validSalespersonIds),
			api.getBranchesByIds(job.branchIds),
		]);

		for (const contact of contacts) {
			contactsToUpdate.push({
				...contact,
				salespersonBranchIds: Array.from(new Set([...(contact.salespersonBranchIds || []), ...job.branchIds!])),
				isSalesperson: true,
			});
		}
		for (const branch of branches) {
			branchesToUpdate.push({
				...branch,
				salespersonIds: Array.from(new Set([...(branch.salespersonIds || []), ...validSalespersonIds])),
			});
		}
	}

	contactsToUpdate = contactsToUpdate.filter(
		(contact, index, self) => contact.id && self.findIndex((c) => c.id === contact.id) === index,
	);
	branchesToUpdate = branchesToUpdate.filter(
		(branch, index, self) => branch.id && self.findIndex((b) => b.id === branch.id) === index,
	);

	if (contactsToUpdate.length === 0 && branchesToUpdate.length === 0) return;

	logData.function = "jobCreationOrchestration.associateSalespersonsAndBranches";
	logger.log("Associating salespersons and branches", logData);

	await api.updateContacts(contactsToUpdate);
	await api.updateBranches(branchesToUpdate);
}

/**
 * Prepares jobs for creation: assigns UUIDs, applies flags, handles composite zones,
 * renumbers cores, and cleans GeoJSON. Pure-ish (uses `generateUUID` + `logger` only).
 */
export function prepareJobsForCreation(
	jobs: Partial<Job>[],
	ctx: JobCreationContext,
	logData: LogData,
): Partial<Job>[] {
	return jobs.map((job) => {
		job.apiId = generateUUID();
		applyCollectBoundary(job);
		job.isPointsAttached = job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0 ? true : false;

		job.jobFlags = buildJobFlags(job.jobFlags, ctx.source, ctx.isAdmin);

		const deal = ctx.deals.find((d) => d.id === job.dealIds?.[0]);
		handleZonesForCompositeJobs(job, deal);

		const hasZones = job.files?.zonesGeoJSON && job.files.zonesGeoJSON.features?.length > 0;
		const hasPoints = job.files?.pointsGeoJSON && job.files.pointsGeoJSON.features?.length > 0;

		if (hasZones && hasPoints) {
			logData.function = "jobCreationOrchestration.prepareJobsForCreation";
			ctx.logger.log("Renumbering zone cores", { ...logData, temp_id: job.apiId });
			job.files!.pointsGeoJSON = renumberZoneCores(job.files!.zonesGeoJSON!, job.files!.pointsGeoJSON!);
		}

		if (job.files?.boundaryGeoJSON) job.files.boundaryGeoJSON = cleanGeoJson(job.files.boundaryGeoJSON, "boundary");
		if (job.files?.pointsGeoJSON)
			job.files.pointsGeoJSON = normalizePointsFC(cleanGeoJson(job.files.pointsGeoJSON, "point"));
		if (job.files?.zonesGeoJSON) job.files.zonesGeoJSON = cleanGeoJson(job.files.zonesGeoJSON, "sample zone");

		return job;
	});
}
